import React, { useState } from 'react'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { Button, Stack, Text } from '@ui'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { ButtonSpinner } from 'ui/components/Spinner/ButtonSpinner'
import {
    blockchainQueryKeys,
    queryClient,
    SignerUndefinedError,
    SpaceIdFromSpaceAddress,
    TransactionStatus,
    useMyUserId,
    usePauseSubscription,
} from 'use-towns-client'
import { Address } from 'viem'

import { trackSubscriptionUpdate } from './subscriptionAnalytics'
import { readableDuration, useSubscriptionDisplayData } from './SubscriptionsPanel'

export function ConfirmDisableRenewal(props: {
    spaceAddress: Address
    spaceName: string
    expiryDate: string
    myAddress: Address
    tokenId: string
    entityId: number
    subscriptionDisplayData: ReturnType<typeof useSubscriptionDisplayData>
    onCancel: () => void
}) {
    const {
        onCancel,
        myAddress,
        spaceAddress,
        tokenId,
        entityId,
        spaceName,
        expiryDate,
        subscriptionDisplayData,
    } = props
    const myUserId = useMyUserId()
    const [isRefetching, setIsRefetching] = useState(false)

    const trackSubscriptionDisable = (success: boolean) => {
        trackSubscriptionUpdate({
            spaceName,
            spaceId: SpaceIdFromSpaceAddress(spaceAddress),
            duration: readableDuration(subscriptionDisplayData.membershipInfo?.duration ?? 0),
            type: 'disable',
            success,
        })
    }

    const { execute: pauseSubscription, transactionStatus: pauseTransactionStatus } =
        usePauseSubscription({
            onSuccess: async ({ entityId }) => {
                setIsRefetching(true)
                await queryClient.refetchQueries({
                    queryKey: blockchainQueryKeys.membershipStatus(
                        SpaceIdFromSpaceAddress(spaceAddress),
                        myUserId ?? '',
                    ),
                })
                if (myAddress) {
                    await queryClient.refetchQueries({
                        queryKey: blockchainQueryKeys.subscriptions(myAddress),
                    })
                    await queryClient.refetchQueries({
                        queryKey: blockchainQueryKeys.singleSubscription(
                            myAddress,
                            spaceAddress,
                            tokenId,
                        ),
                    })
                }
                popupToast(({ toast }) => (
                    <StandardToast.Success toast={toast} message="Auto-renewal disabled" />
                ))

                trackSubscriptionDisable(true)

                onCancel()
            },
            onError: ({ entityId, error }) => {
                setIsRefetching(false)
                popupToast(({ toast }) => (
                    <StandardToast.Error toast={toast} message="Failed to disable auto-renewal" />
                ))
                trackSubscriptionDisable(false)
            },
        })
    const isUninstalling = pauseTransactionStatus === TransactionStatus.Pending || isRefetching

    const handlePause = async (getSigner: GetSigner) => {
        const signer = await getSigner()
        if (!signer) {
            throw new SignerUndefinedError()
        }

        pauseSubscription({
            entityId,
            signer,
        })
    }

    return (
        <ModalContainer minWidth="350" onHide={onCancel}>
            <Stack padding="sm" gap="lg" alignItems="start" maxWidth="350">
                <Text fontWeight="strong">
                    {isUninstalling
                        ? 'Turning off auto-renewal'
                        : `Turn off auto-renewal for ${spaceName}?`}
                </Text>
                {isUninstalling ? (
                    <Stack horizontal gap alignItems="center">
                        <ButtonSpinner color="gray2" />
                        <Text color="gray2">Please wait</Text>
                    </Stack>
                ) : (
                    <>
                        <Text>
                            If you turn off auto-renewal, you will lose access to this town on{' '}
                            {expiryDate}
                        </Text>
                        <Stack horizontal gap width="100%">
                            <Stack grow />
                            <Button
                                tone="level2"
                                disabled={isUninstalling}
                                rounded="lg"
                                onClick={onCancel}
                            >
                                Cancel
                            </Button>
                            <WalletReady>
                                {({ getSigner }) => (
                                    <Button
                                        tone="negative"
                                        disabled={isUninstalling}
                                        rounded="lg"
                                        color="default"
                                        onClick={async () => {
                                            await handlePause(getSigner)
                                        }}
                                    >
                                        Confirm
                                    </Button>
                                )}
                            </WalletReady>
                        </Stack>
                    </>
                )}
            </Stack>
        </ModalContainer>
    )
}
